import * as React from "react";
import * as S from "@styled";
import Select from "shared/components/select/Select";
import { connect } from "react-redux";
import { Triggers } from "../model/ITriggerData";
import { IDocumentationState, State } from "../model/State";
import { FileWatchTrigger } from "./Triggers/FileWatchTrigger";
import { HttpApiTrigger } from "./Triggers/HttpApiTrigger";
import { HttpTrigger } from "./Triggers/HttpTrigger";
import { default as ManualTrigger } from "./Triggers/ManualTrigger";
import { QueueTrigger } from "./Triggers/QueueTrigger";
import { default as ScheduleTrigger } from "./Triggers/ScheduleTrigger";
import { ServiceBusTrigger } from "./Triggers/ServiceBusTrigger";
import { RabbitMQTrigger } from "./Triggers/RabbitMQTrigger";

import { DispatchType, Utilities } from "../../shared/utilities";
import { selectConditionalProcess } from "../actions/SelectConditionalProcess";
import { ValidationError } from "../model/ValidationData";
import LogResultEditor from "./LogResultEditor";
import { ConditionalTrigger } from "./Triggers/ConditionalTrigger";
import { ITypeSelectorModel } from "./TypeSelector";
import { Option } from "shared/model/Option";
import { updateDocumentationPanel } from "../actions/UpdateDocumentationPanelActions";
import { ISimpleApiSpecificationData } from "apiManagement/model/model";
import { TcpTrigger } from "./Triggers/TcpTrigger";

export const TriggerEditor: React.FC<TriggerEditorProps> = (props) => {

    const [currentTrigger, setCurrentTrigger] = React.useState<string | undefined>();

    const handleChange = (e: Option) => {
        props.onChange(e.value, (Triggers[e.value] as any).defaultConfig);
    }

    const getTriggerSpecificConfiguration = () => {
        switch (props.selectedTrigger) {
            case Triggers.FileWatchTrigger.triggerType:
                return <FileWatchTrigger existingParameters={props.existingConfig} id={props.id} />;
            case Triggers.HttpTrigger.triggerType:
                return <HttpTrigger existingParameters={props.existingConfig} id={props.id} />;
            case Triggers.QueueTrigger.triggerType:
                return <QueueTrigger existingParameters={props.existingConfig} id={props.id} />;
            case Triggers.ScheduleTrigger.triggerType:
                return <ScheduleTrigger existingParameters={props.existingConfig} id={props.id} />;
            case Triggers.ConditionalTrigger.triggerType:
                return <ConditionalTrigger
                    existingParameters={props.existingConfig}
                    id={props.id}
                    subProcesses={props.subProcessSelectorModel}
                    onConditionalProcessSelected={props.onConditionalProcessSelected}
                    validationErrors={props.validationErrors}
                />;
            case Triggers.ServiceBusTrigger.triggerType:
                return <ServiceBusTrigger existingParameters={props.existingConfig} id={props.id} />;
            case Triggers.RabbitMQTrigger.triggerType:
                return <RabbitMQTrigger existingParameters={props.existingConfig} id={props.id} />;
            case Triggers.ManualTrigger.triggerType:
                return <ManualTrigger id={props.id} existingParameters={props.existingConfig} />;
            case Triggers.HttpApiTrigger.triggerType:
                return <HttpApiTrigger id={props.id} existingParameters={props.existingConfig} apiSpecs={props.apiSpecs} />;
            case Triggers.TcpTrigger.triggerType:
                return <TcpTrigger existingParameters={props.existingConfig} id={props.id} />;
            default:
                return null;
        }
    }

    const triggersOptions: Option[] = Object.keys(Triggers)
        // Hide manual trigger if it's not the selected trigger
        .filter((trigger) => trigger !== Triggers.ManualTrigger.triggerType || props.selectedTrigger === Triggers.ManualTrigger.triggerType)
        // Filter out tcp trigger if it's not enabled or the selected trigger
        .filter((trigger) => 
            trigger !== Triggers.TcpTrigger.triggerType || 
            props.selectedTrigger === Triggers.TcpTrigger.triggerType || 
            props.tcpTriggerEnabled
        )
        .map((trigger) => ({
            label: Triggers[trigger].displayName,
            value: trigger
        }));

    const getDocumentationForElementType = async () => {
        try {
            const triggerDisplayName = Object.values(Triggers).find((f) => f.triggerType === props.selectedTrigger)?.displayName;
            if (triggerDisplayName) {
                props.handleUpdateDocumentationPanel({ identifier: triggerDisplayName, type: "trigger" });
            }
        } catch (e) {
            console.error(e);
            Utilities.toastError(e);
        }
    }

    React.useEffect(() => {
        if (props.selectedTrigger !== currentTrigger) {
            getDocumentationForElementType();
            setCurrentTrigger(props.selectedTrigger);
        }
    }, [props.selectedTrigger])

    return (
        <div>
            <S.FormGroup>
                <S.Label>{FRENDS.Localizations.Type}</S.Label>
                <Select
                    classNamePrefix="react-select"
                    id="trigger-type-selector"
                    value={Utilities.valueFromId(triggersOptions, props.selectedTrigger)}
                    options={triggersOptions}
                    onChange={handleChange}
                    isDisabled={props.isSubprocess || props.selectedTrigger === Triggers.HttpApiTrigger.triggerType || props.disabled}
                />
            </S.FormGroup>
            <hr />
            {getTriggerSpecificConfiguration()}

            {// Do not allow turning of start element logging for manual triggers as we can't block that
                (props.isSubprocess || props.selectedTrigger !== Triggers.ManualTrigger.triggerType) ?
                    <LogResultEditor label={FRENDS.Localizations.ProcessEditor_OmitTriggerParameters} elementId={props.id} />
                    : null}

        </div>
    );

}

interface TriggerEditorOwnProps {
    id: string;
    name?: string;
    onChange: (selectedTrigger: any, triggerConfig: any) => void;
}

interface TriggerEditorStateProps {
    selectedTrigger: string | undefined;
    existingConfig: any;
    environmentVariables: string[];
    subProcessSelectorModel: ITypeSelectorModel[];
    validationErrors: ValidationError[];
    isSubprocess: boolean | undefined;
    disabled?: boolean;
    tcpTriggerEnabled?: boolean;
    apiSpecs: ISimpleApiSpecificationData[];
}

interface TriggerEditorDispatchProps {
    onConditionalProcessSelected: (processGuid: string) => void;
    handleUpdateDocumentationPanel: (documentation: IDocumentationState) => void;
}

type TriggerEditorProps = TriggerEditorOwnProps & TriggerEditorStateProps & TriggerEditorDispatchProps;

const mapTriggerEditorStateToProps = (state: State, ownProps: TriggerEditorOwnProps): TriggerEditorStateProps => {
    const selectedElement = state.elements[ownProps.id];
    const subProcessSelectorModel = state.subProcesses.map((subProcess) => {
        return { value: subProcess.uniqueIdentifier, label: subProcess.name, title: subProcess.description };
    });
    return {
        existingConfig: selectedElement.parameters,
        selectedTrigger: selectedElement.selectedTypeId,
        environmentVariables: state.environmentVariables || [],
        subProcessSelectorModel: subProcessSelectorModel,
        validationErrors: state.validation.validationDictionary[ownProps.id],
        isSubprocess: state.process.isSubprocess,
        disabled: state.uiState.disableEdit,
        apiSpecs: state.apiSpecs,
        tcpTriggerEnabled: state.uiState.tcpTriggerEnabled
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: TriggerEditorOwnProps): TriggerEditorDispatchProps => {
    return {
        onConditionalProcessSelected: (processGuid: string) => dispatch(selectConditionalProcess(ownProps.id, processGuid)),
        handleUpdateDocumentationPanel: (documentation: IDocumentationState) => dispatch(updateDocumentationPanel(documentation))
    };
};

export default connect(mapTriggerEditorStateToProps, mapDispatchToProps)(TriggerEditor);
